import { useEffect } from "react";
import {
  getSupabaseTenantSnapshot,
  isSupabaseTenantSnapshotCurrent,
} from "../lib/tenantTransport";

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

export const TenantServiceWorkerBridge = () => {
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const handleMessage = async (event: MessageEvent) => {
      const snapshot = getSupabaseTenantSnapshot();
      if (!snapshot.tenantId) return;

      try {
        if (event.data?.type === "SYNC_OFFLINE_SALES") {
          console.warn(
            "[SW] Offline sales remain local until atomic synchronization is available.",
          );
          return;
        }

        if (event.data?.type !== "SYNC_PERIODIC_DATA") return;

        const [{ cacheInventory, cacheCustomers }, { supabase }] =
          await Promise.all([
            import("../lib/offlineQueue"),
            import("../lib/supabase"),
          ]);
        if (!isSupabaseTenantSnapshotCurrent(snapshot)) return;

        const [inventoryResult, customersResult] = await Promise.all([
          supabase
            .from("inventory")
            .select("*")
            .eq("tenant_id", snapshot.tenantId),
          supabase
            .from("customers")
            .select("*")
            .eq("tenant_id", snapshot.tenantId),
        ]);
        if (!isSupabaseTenantSnapshotCurrent(snapshot)) return;

        if (inventoryResult.data) {
          await cacheInventory(snapshot.tenantId, inventoryResult.data);
        }
        if (!isSupabaseTenantSnapshotCurrent(snapshot)) return;
        if (customersResult.data) {
          await cacheCustomers(snapshot.tenantId, customersResult.data);
        }
      } catch (error) {
        if (!isAbortError(error)) {
          console.error("[SW] Tenant-scoped synchronization failed:", error);
        }
      }
    };

    const listener = (event: MessageEvent) => {
      void handleMessage(event);
    };
    navigator.serviceWorker.addEventListener("message", listener);
    return () => {
      navigator.serviceWorker.removeEventListener("message", listener);
    };
  }, []);

  return null;
};
